import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ICONS } from "src/assests";

export type SearchBarSize = "sm" | "md" | "lg";

export interface SearchBarProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onDebouncedChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  debounceMs?: number;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  showClear?: boolean;
  size?: SearchBarSize;
  name?: string;
  ariaLabel?: string;
  /** Rendered inside the input on the right (e.g. info icon). Input gets extra right padding when set. */
  rightAdornment?: React.ReactNode;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  defaultValue,
  onChange,
  onDebouncedChange,
  onSubmit,
  debounceMs = 300,
  placeholder = "Search...",
  className = "",
  inputClassName = "",
  disabled = false,
  autoFocus = false,
  showClear = true,
  size = "md",
  name,
  ariaLabel = "Search",
  rightAdornment,
}) => {
  const isControlled = typeof value === "string";
  const [internalValue, setInternalValue] = useState<string>(defaultValue || "");
  const currentValue = isControlled ? value || "" : internalValue;
  const debounceTimerRef = useRef<number | null>(null);

  const sizeClasses = useMemo(() => {
    switch (size) {
      case "sm":
        return {
          wrapper: "h-9",
          input: "text-sm py-1.5 pl-9 pr-9",
          icon: "h-4 w-4 left-3",
          clear: "h-5 w-5",
        };
      case "lg":
        return {
          wrapper: "h-12",
          input: "text-base py-3 pl-11 pr-11",
          icon: "h-5 w-5 left-3",
          clear: "h-5 w-5",
        };
      case "md":
      default:
        return {
          wrapper: "h-11",
          input: "text-sm py-3 pl-10 pr-10",
          icon: "h-4 w-4 left-3",
          clear: "h-5 w-5",
        };
    }
  }, [size]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      if (isControlled) {
        onChange?.(next);
      } else {
        setInternalValue(next);
        onChange?.(next);
      }
    },
    [isControlled, onChange]
  );

  const handleClear = useCallback(() => {
    if (disabled) return;
    if (isControlled) {
      onChange?.("");
    } else {
      setInternalValue("");
      onChange?.("");
    }
    onDebouncedChange?.("");
  }, [disabled, isControlled, onChange, onDebouncedChange]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit?.(currentValue);
    },
    [currentValue, onSubmit]
  );

  useEffect(() => {
    if (!onDebouncedChange) return;
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      onDebouncedChange(currentValue);
    }, debounceMs) as unknown as number;
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, [currentValue, debounceMs, onDebouncedChange]);

  return (
    <form onSubmit={handleSubmit} className={`relative ${sizeClasses.wrapper} ${className}`}>
      {/* Search icon */}
      <span className={`absolute top-1/2 -translate-y-1/2 left-[16px] ${sizeClasses.icon} flex items-center text-gray-400`}>
        <img src={ICONS.SearchIcon} alt="Search Icon" className="w-[18px] h-[18px]" />
      </span>

      <input
        type="text"
        name={name}
        aria-label={ariaLabel}
        disabled={disabled}
        autoFocus={autoFocus}
        value={currentValue}
        onChange={handleChange}
        placeholder={placeholder}
        className={`w-full h-full rounded-lg border border-[#F1F1F2] bg-white text-[#252525] placeholder:text-[#9B9B9B] focus:outline-none ${sizeClasses.input} ${rightAdornment ? "!pr-9" : ""} ${inputClassName}`}
      />

      {showClear && !!currentValue && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${rightAdornment ? "right-8" : "right-2"}`}
          aria-label="Clear search"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={sizeClasses.clear}>
            <path fillRule="evenodd" d="M6.225 4.811a.75.75 0 011.06 0L12 9.525l4.715-4.714a.75.75 0 111.06 1.06L13.06 10.586l4.715 4.714a.75.75 0 11-1.06 1.06L12 11.646l-4.715 4.714a.75.75 0 11-1.06-1.06l4.714-4.715-4.714-4.715a.75.75 0 010-1.06z" clipRule="evenodd" />
          </svg>
        </button>
      )}
      {rightAdornment && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto flex items-center">
          {rightAdornment}
        </div>
      )}
    </form>
  );
};

export default SearchBar;


